// ═══════════════════════════════════════════════════════════
// Staff Layout — OG CDS AppShell with top nav
// PSPSuiteBar (56px) + nav masthead (44px) + content
// Hamburger opens PSPMenu (left overlay)
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  IconLayoutDashboard, IconInbox, IconChartBar, IconSettings, IconLibrary, IconScissors,
} from '@tabler/icons-react';
import { PSPSuiteBar, PSPMenu, PSPAssistPanel } from '../components/layout/PSPSuiteBar';
import { useAuthStore } from '../store/authStore';
import { useRequestStore } from '../store/useRequestStore';
import { useRealtimeEvents } from '../hooks/useRealtimeEvents';

const NAV = [
  { label: 'Dashboard', path: '/staff',            icon: IconLayoutDashboard, exact: true  },
  { label: 'Requests',  path: '/staff/requests',   icon: IconInbox                         },
  { label: 'Analytics', path: '/staff/analytics',   icon: IconChartBar                      },
  { label: 'Library',   path: '/staff/library',     icon: IconLibrary                       },
  { label: 'Non-FOIA Redaction Tool', path: '/staff/redact', icon: IconScissors            },
];

export default function StaffLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [assistOpen, setAssistOpen] = useState(false);

  useRealtimeEvents();

  const actionCount = useRequestStore(s =>
    s.requests.filter(r => r.status === 'submitted' || r.status === 'pendingReview').length
  );

  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('') || '??';
  const fullName = user?.fullName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '';

  function handleSignOut() {
    logout();
    navigate('/login');
  }

  return (
    <div className="app-shell">
      {/* Suite bar */}
      <PSPSuiteBar
        entity={user?.email ?? ''}
        userInitials={initials}
        userName={fullName}
        userEmail={user?.email ?? ''}
        notifBadge={actionCount}
        assistOpen={assistOpen}
        onMenu={() => setMenuOpen(true)}
        onAssist={() => setAssistOpen(o => !o)}
        onSettings={() => navigate('/staff/settings')}
        onSignOut={handleSignOut}
      />

      {/* Nav masthead */}
      <nav className="app-topnav">
        {NAV.map(({ label, path, icon: Icon, exact }) => (
          <NavLink
            key={path}
            to={path}
            end={exact}
            className={({ isActive }) => `topnav-link${isActive ? ' active' : ''}`}
          >
            <Icon size={14} />
            {label}
          </NavLink>
        ))}

        {/* Settings pushed right */}
        <div style={{ marginLeft: 'auto' }}>
          <NavLink
            to="/staff/settings"
            className={({ isActive }) => `topnav-link${isActive ? ' active' : ''}`}
          >
            <IconSettings size={14} />
            Settings
          </NavLink>
        </div>
      </nav>

      {/* Body: content + assist panel */}
      <div className="app-body">
        <main className="app-main">
          <Outlet />
        </main>
        <PSPAssistPanel open={assistOpen} onClose={() => setAssistOpen(false)} />
      </div>

      {/* Hamburger menu overlay */}
      <PSPMenu open={menuOpen} entity={user?.email ?? ''} onClose={() => setMenuOpen(false)} />

      {/* Mobile bottom nav — hidden on desktop via CSS */}
      <nav className="mobile-bottom-nav" aria-label="Main navigation">
        {NAV.map(({ label, path, icon: Icon, exact }) => (
          <NavLink
            key={path}
            to={path}
            end={exact}
            className={({ isActive }) => `mobile-nav-item${isActive ? ' active' : ''}`}
          >
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <Icon size={20} />
            </span>
            <span>{label}</span>
          </NavLink>
        ))}
        <NavLink
          to="/staff/settings"
          className={({ isActive }) => `mobile-nav-item${isActive ? ' active' : ''}`}
        >
          <IconSettings size={20} />
          <span>Settings</span>
        </NavLink>
      </nav>
    </div>
  );
}
